import {Component, inject} from '@angular/core';
import {HasPermissionDirective} from "../../../directives/permissions/has-permission";
import {MatIcon} from "@angular/material/icon";
import {MatIconButton} from "@angular/material/button";
import {MatMenu, MatMenuItem, MatMenuTrigger} from "@angular/material/menu";
import {MatTooltip} from "@angular/material/tooltip";
import {PermissionTypes} from "../../../enum/PermissionTypes";
import {ICellRendererParams} from "ag-grid-community";
import {ICellRendererAngularComp} from "ag-grid-angular";
import {AutoplayService} from "../../../services/auto-play.service";
import {GameService} from "../../../services/game.service";
import {PlayersService} from "../../../services/players.service";
import {DialogFunctionService} from "../../../services/dialog-function.service";
import {IDialogGenericData} from "../../../interfaces/IDialogGenericData";
import {ConfirmationActionDialogComponent} from "../../dialogs/confirmation-action-dialog/confirmation-action-dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {UIStateEnum} from "../../../enum/UIStateEnum";
import {Observable, tap} from "rxjs";
import {switchMap} from "rxjs/operators";
import {ErrorHandlingService} from "../../../services/error-handling.service";

@Component({
  selector: 'app-fifty-fifty-autoplay-settings',
  standalone: true,
  imports: [
    HasPermissionDirective,
    MatIcon,
    MatIconButton,
    MatMenu,
    MatMenuItem,
    MatTooltip,
    MatMenuTrigger
  ],
  templateUrl: './fifty-fifty-autoplay-settings.component.html',
  styleUrl: './fifty-fifty-autoplay-settings.component.css'
})
export class FiftyFiftyAutoplaySettingsComponent implements ICellRendererAngularComp {
  params!: ICellRendererParams;
  public permissionTypes = PermissionTypes;

  private autoplayService = inject(AutoplayService);
  private dialogFunctionService = inject(DialogFunctionService);
  private matDialog = inject(MatDialog);
  private playerService = inject(PlayersService);
  private gameService = inject(GameService);
  private errorHandlingService = inject(ErrorHandlingService);

  public uiState: UIStateEnum = UIStateEnum.ShowData;

  agInit(params: ICellRendererParams): void {
    this.params = params;
  }

  refresh(_params: ICellRendererParams): boolean {
    return false;
  }

  get rowData() {
    return this.params?.data;
  }

  public onToggleAutoplay(): void {
    const isEnabling = !this.rowData.Active;
    const displayPrice = (this.rowData.Price / 100).toFixed(2);

    const command = {
      AutoplayGroupId: this.rowData.Id,
      Active: isEnabling,
      PlayerId: this.rowData.PlayerId,
      GameId: this.rowData.GameId
    };

    const dialogData: IDialogGenericData = {
      title: `${isEnabling ? 'Enable' : 'Disable'} Autoplay Group`,
      message: `By ${isEnabling ? 'enabling' : 'disabling'} this autoplay group, the player will ${isEnabling ? 'be charged' : 'no longer be charged'} $${displayPrice} for ${this.rowData.NumberOfTickets} tickets each week. Please confirm you have acknowledged this with the user.`,
      successMessage: `Autoplay group ${isEnabling ? 'enabled' : 'disabled'} successfully`,
      dataCyTag: 'confirm-autoplay-toggle'
    };

    this.showConfirmationDialog(dialogData, () =>
      this.autoplayService.updateAutoplayGroupStatus(command)
    ).subscribe({
      error: (err) => {
        this.uiState = UIStateEnum.ShowData;
        this.errorHandlingService.displayPageLevelErrorMessage(err, 'Failed to update autoplay group status');
      }
    });
  }

  private showConfirmationDialog(dialogData: IDialogGenericData, apiCall: () => Observable<any>): Observable<any> {
    this.dialogFunctionService.setCallback(() => {
      this.uiState = UIStateEnum.ShowLoading;
      return apiCall();
    });

    return this.matDialog.open(ConfirmationActionDialogComponent, {
      width: '450px',
      data: dialogData
    }).afterClosed().pipe(
      tap(result => {
        if (result) {
          this.playerService.fetchUpdatedTicketNumbersSubject.next(true);
        }
        this.uiState = UIStateEnum.ShowData;
      })
    );
  }
}
